import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { UserPlus, Edit, Trash2, Key, UserCheck, UserX, ArrowLeft } from 'lucide-react';

export default function UserManagement() {
  const { token, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    active: true
  });
  const [passwordData, setPasswordData] = useState({
    userId: null,
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!loading) {
      if (!token) {
        navigate('/admin/login');
      } else {
        loadUsers();
      }
    }
  }, [loading, token, navigate]); // Combinei os dois useEffects para melhor controle

  const loadUsers = async () => {
    try {
      const data = await adminApi.getAllUsers(token);
      setUsers(data);
    } catch (error) {
      console.error('Erro ao carregar utilizadores:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os utilizadores"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      active: true
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      active: user.active
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este utilizador?')) {
      return;
    }

    try {
      await adminApi.deleteUser(token, userId);
      toast({
        title: "Sucesso",
        description: "Utilizador eliminado com sucesso"
      });
      loadUsers();
    } catch (error) {
      console.error('Erro ao eliminar utilizador:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível eliminar o utilizador"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update user
        await adminApi.updateUser(token, editingUser.id, {
          name: formData.name,
          email: formData.email,
          active: formData.active
        });
        toast({
          title: "Sucesso",
          description: "Utilizador atualizado com sucesso"
        });
      } else {
        // Create user
        if (!formData.password) {
          toast({
            variant: "destructive",
            title: "Erro",
            description: "A palavra-passe é obrigatória"
          });
          return;
        }
        await adminApi.createUser(token, formData);
        toast({
          title: "Sucesso",
          description: "Utilizador criado com sucesso"
        });
      }
      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error('Erro ao salvar utilizador:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.response?.data?.detail || "Não foi possível salvar o utilizador"
      });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await adminApi.updateUser(token, user.id, {
        active: !user.active
      });
      toast({
        title: "Sucesso",
        description: `Utilizador ${!user.active ? 'ativado' : 'desativado'} com sucesso`
      });
      loadUsers();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o status do utilizador"
      });
    }
  };

  const handleChangePassword = (userId) => {
    setPasswordData({
      userId,
      password: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.password !== passwordData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "As palavras-passe não coincidem"
      });
      return;
    }

    if (passwordData.password.length < 6) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "A palavra-passe deve ter pelo menos 6 caracteres"
      });
      return;
    }

    try {
      await adminApi.updateUserPassword(token, passwordData.userId, passwordData.password);
      toast({
        title: "Sucesso",
        description: "Palavra-passe atualizada com sucesso"
      });
      setShowPasswordModal(false);
    } catch (error) {
      console.error('Erro ao atualizar palavra-passe:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a palavra-passe"
      });
    }
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Gerenciar Utilizadores
            </h1>
          </div>
          <Button 
            onClick={handleCreate}
            className="bg-[#2E5E99] hover:bg-[#0D2440] flex items-center gap-2 w-full md:w-auto"
          >
            <UserPlus className="w-4 h-4" />
            Novo Utilizador
          </Button>
        </div>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Utilizadores do Sistema</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data de Criação</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleChangePassword(user.id)}
                            title="Alterar Palavra-passe"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(user)}
                            title={user.active ? 'Desativar' : 'Ativar'}
                          >
                            {user.active ? (
                              <UserX className="w-4 h-4 text-red-500" />
                            ) : (
                              <UserCheck className="w-4 h-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            className="text-red-500 hover:text-red-700"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create/Edit User Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                {!editingUser && (
                  <div>
                    <Label htmlFor="password">Palavra-passe</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingUser}
                      minLength={6}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="active">Utilizador ativo</Label>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#2E5E99] hover:bg-[#0D2440]">
                  {editingUser ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change Password Modal */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Alterar Palavra-passe</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="newPassword">Nova Palavra-passe</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirmar Palavra-passe</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-[#2E5E99] hover:bg-[#0D2440]">
                  Atualizar Palavra-passe
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
